import { cn } from '@woonstad/helpers-utils';
import { MouseEventHandler } from 'react';

import { BasicCheck, BasicClose } from '../../icons/basic';
import { Typography } from '../typography/Typography';

type Props = {
  children: string;
  variant?: 'default' | 'removable' | 'checked';
  size?: 'small' | 'large';
  state?: 'enabled' | 'selected';
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

const InnerChip = ({
  children,
  size,
  state,
  variant,
  onClick,
}: Props) => {
  const iconClasses = cn(
    'size-4',
    state === 'enabled' && 'text-chip-enabled-icon',
    state === 'selected' && 'text-chip-selected-icon',
    onClick && 'group-hover:text-chip-enabled-hover-icon group-active:text-chip-activated-icon',
  );

  return (
    <Typography
      variant={size === 'small' ? 'label-small' : 'content-body'}
      className="flex items-center justify-center gap-2"
      type="span"
    >
      {variant === 'checked' && <BasicCheck className={iconClasses} />}
      {children}
      {variant === 'removable' && <BasicClose className={iconClasses} />}
    </Typography>
  );
};

/**
 * Goal
 *
 * Chips communicate a selection or active filter. Chips can be used to alter a selection or filter.
 *
 * Usage
 *
 * - Removable Chips
 *   - Several Chips are placed together, reflecting selection(s) made elsewhere.
 *   - For example: the Chips are positioned at the top of a facets sidebar, showing which filters are selected.
 *   - Each Chip has a ‘close’ icon at the right, indicating that the Chip can be “removed”.
 *   - On clicking a Chip, it is removed from the list, turning off the represented filter (keeps the user from having to find the filter itself)
 * - Single-select Chips
 *   - Several Chips are be placed together, 1 Chip selected.
 *   - When an inactive Chip is selected, the active Chip is deselected (like a radio button group).
 * - Multi-select Chips
 *   - Serveral Chips are placed together.
 *   - Multiple Chips can be selected at the same time (like a checkbox group).
 *   - Note that Tags - although visually somewhat similar to Chips - are static and non-interactive.
 */
export const Chip = ({
  children,
  variant = 'default',
  state = 'enabled',
  size = 'small',
  onClick,
}: Props) => {
  const classes = cn(
    'group flex w-fit items-center justify-center whitespace-nowrap rounded-full px-3 py-2',
    state === 'enabled' && 'bg-chip-enabled text-chip-enabled',
    state === 'selected' && 'bg-chip-selected text-chip-selected',
    onClick && 'hover:bg-chip-hover hover:text-chip-enabled-hover active:text-chip-activated active:bg-chip-activated',
  );

  if (onClick) {
    return (
      <button
        className={classes}
        onClick={onClick}
        data-testid="removeChipButton"
      >
        <InnerChip
          variant={variant}
          state={state}
          size={size}
          onClick={onClick}
        >
          {children}
        </InnerChip>
      </button>
    );
  }

  return (
    <div className={classes}>
      <InnerChip
        variant={variant}
        state={state}
        size={size}
        onClick={onClick}
      >
        {children}
      </InnerChip>
    </div>
  );
};
